import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useIsMobile } from '../hooks/useIsMobile';
import { createDevice, previewTopic, getSites, getMqttConfig, MqttConfig } from '../services/api';
import { copyToClipboard } from '../utils/copyToClipboard';
import { Device, Site } from '../types';
import OnboardingChecklist from '../components/OnboardingChecklist';

type ConnectionType = 'direct' | 'gateway';
type ParserType = 'direct' | 'devId_field' | 'json_path' | 'custom';

const STEPS = [
  { label: 'Connect device', shortLabel: 'Connect', detail: 'What are you connecting?' },
  { label: 'Confirm topic', shortLabel: 'Topic', detail: 'Review the MQTT route' },
  { label: 'Credentials', shortLabel: 'Credentials', detail: 'Copy the setup packet' },
  { label: 'Finish setup', shortLabel: 'Finish', detail: 'Ready for telemetry' },
];

const parserCopy: Record<Exclude<ParserType, 'direct'>, string> = {
  devId_field: 'Payload contains a devId field matched against the device identifier.',
  json_path: 'Payload is keyed by a device identifier such as {"VI_9207": {...}}.',
  custom: 'Use custom JSON mappings for payloads that do not match standard layouts.',
};

function normalizeEditableNumber(value: string, fallback: number) {
  if (value.trim() === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

export default function OnboardingWizard() {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [connectionType, setConnectionType] = useState<ConnectionType>('direct');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [heartbeatTimeout, setHeartbeatTimeout] = useState('30');
  const [gatewayTopic, setGatewayTopic] = useState('');
  const [deviceIdentifier, setDeviceIdentifier] = useState('');
  const [parserType, setParserType] = useState<ParserType>('direct');
  const [parserConfigJson, setParserConfigJson] = useState('');
  const [device, setDevice] = useState<Device | null>(null);
  const [previewedTopic, setPreviewedTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [mqttConfig, setMqttConfig] = useState<MqttConfig | null>(null);
  const [useTls, setUseTls] = useState(true);

  useEffect(() => {
    getSites().then(setSites).catch(() => {});
    getMqttConfig()
      .then((config) => {
        setMqttConfig(config);
        if (!config.tls_port) setUseTls(false);
      })
      .catch(() => {});
  }, []);

  const brokerHost = mqttConfig?.host || window.location.hostname;
  const tlsAvailable = Boolean(mqttConfig?.tls_port);
  const brokerPort = useTls && mqttConfig?.tls_port ? mqttConfig.tls_port : mqttConfig?.port || 1883;
  const brokerUrl = `${useTls && tlsAvailable ? 'mqtts' : 'mqtt'}://${brokerHost}:${brokerPort}`;

  const selectedSite = sites.find((site) => site.id === siteId);
  const selectedArea = selectedSite?.areas.find((area) => area.id === areaId);
  const gatewayParserType: Exclude<ParserType, 'direct'> =
    parserType === 'direct' ? 'devId_field' : parserType;
  const heartbeatTimeoutMinutes = normalizeEditableNumber(heartbeatTimeout, 30);

  async function handleCreate() {
    setLoading(true);
    setError('');
    try {
      const payload: any = {
        name,
        location: location || undefined,
        topic: previewedTopic,
        heartbeat_timeout_minutes: heartbeatTimeoutMinutes,
        area_id: areaId || undefined,
      };

      if (connectionType === 'gateway') {
        payload.gateway_topic = gatewayTopic;
        payload.device_identifier = deviceIdentifier;
        payload.parser_type = gatewayParserType;
        if (parserConfigJson.trim()) {
          try {
            payload.parser_config = JSON.parse(parserConfigJson);
          } catch {
            setError('Parser config JSON is invalid.');
            setLoading(false);
            return;
          }
        }
      }

      const created = await createDevice(payload);
      setDevice(created);
      setStep(2);
    } catch {
      setError('Failed to create device. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function copy(text: string, label: string) {
    const ok = await copyToClipboard(text);
    if (!ok) {
      window.alert('Copy failed. Please copy manually.');
      return;
    }
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  function isStep1Valid(): boolean {
    if (!name.trim()) return false;
    if (connectionType === 'gateway') {
      if (!gatewayTopic.trim()) return false;
      if (!deviceIdentifier.trim()) return false;
    }
    return true;
  }

  async function goToPreview() {
    if (!isStep1Valid()) return;
    setError('');
    setPreviewLoading(true);
    try {
      const { topic } = await previewTopic({ name, location: location || undefined });
      setPreviewedTopic(topic);
      setStep(1);
    } catch {
      setError('Failed to generate topic preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  }

  function chooseConnectionType(nextType: ConnectionType) {
    setConnectionType(nextType);
    if (nextType === 'gateway' && parserType === 'direct') {
      setParserType('devId_field');
    }
    if (nextType === 'direct') {
      setParserType('direct');
    }
  }

  function resetWizard() {
    setStep(0);
    setConnectionType('direct');
    setName('');
    setLocation('');
    setSiteId('');
    setAreaId('');
    setHeartbeatTimeout('30');
    setGatewayTopic('');
    setDeviceIdentifier('');
    setParserType('direct');
    setParserConfigJson('');
    setDevice(null);
    setPreviewedTopic('');
    setError('');
  }

  const configJson = device
    ? JSON.stringify(
        {
          device_name: device.name,
          mqtt_broker: brokerUrl,
          mqtt_username: device.topic, // device topic = MQTT username (auth identity)
          api_key: device.api_key, // API key = MQTT password
          publish_topic: device.gateway_topic || device.topic, // always publish HERE
          ...(device.device_identifier ? { device_identifier: device.device_identifier } : {}),
        },
        null,
        2
      )
    : '';

  const publishTopic = device ? device.gateway_topic || device.topic : '';
  const isGatewayDevice = Boolean(device?.gateway_topic);

  const codeExample = device
    ? `// Publish sensor data with MQTT username/password auth
const mqtt = require('mqtt');

// Username = device topic (your MQTT identity)
// Password = device API key
const client = mqtt.connect('${brokerUrl}', {
  username: "${device.topic}",
  password: "${device.api_key}",
});

client.on('connect', () => {
  const payload = JSON.stringify({${isGatewayDevice ? `
    devId: "${device.device_identifier}", // required by the parser to map this reading` : ''}
    temperature: 24.5,
    humidity: 62.0,
    pressure: 1013.2,
    battery: 94
  });

  // IMPORTANT: publish to the ${isGatewayDevice ? 'gateway topic' : 'device topic'}${isGatewayDevice ? ' — NOT the username' : ''}.
  // Publishing anywhere else is rejected by the broker ACL (silently at QoS 0).
  client.publish("${publishTopic}", payload);
  console.log('Data published to ${publishTopic}');
});`
    : '';
  const currentStep = STEPS[step];
  const onboardingPercent = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <section className="sd-page-stack sd-onboard-page">
      <div className="sd-page-header sd-onboard-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">DEVICE COMMISSIONING</div>
          <h1>Onboard Device</h1>
          <p>Guided setup for direct sensors and gateway-backed assets.</p>
        </div>
        <div className="sd-page-actions">
          <div className="sd-onboard-time-chip">2-3 min setup</div>
          <div className="sd-status-pill is-online">
            <span />
            Step {step + 1} of {STEPS.length}
          </div>
        </div>
      </div>

      <div className="sd-onboard-shell">
        <article className="sd-panel sd-onboard-flow">
          <OnboardingStepper currentStep={step} onSelect={setStep} />

          <div className="sd-onboard-flow-content">
          {step === 0 && (
            <>
              <div className="sd-onboard-section-head">
                <span>{currentStep.label}</span>
                <h2>Tell us what you are connecting</h2>
                <p>Choose the telemetry path, then name the asset operators will see.</p>
              </div>

              <div className="sd-onboard-body">
                {error && <div className="sd-onboard-error">{error}</div>}

                <div className="sd-onboard-choice-grid" aria-label="Connection type">
                  <button
                    className={`sd-onboard-choice-card ${connectionType === 'direct' ? 'is-selected' : ''}`}
                    onClick={() => chooseConnectionType('direct')}
                    type="button"
                  >
                    <span className="sd-onboard-choice-top">
                      <span className="sd-onboard-choice-icon is-direct">
                        <DirectSensorIcon />
                      </span>
                      <span className="sd-onboard-choice-check" aria-hidden="true" />
                    </span>
                    <strong>Direct Sensor</strong>
                    <em>Recommended</em>
                    <small>One sensor publishes directly to Seedronics with its own topic and API key.</small>
                    <span className="sd-onboard-choice-points">
                      <span>Plug-and-play</span>
                      <span>Live data in seconds</span>
                    </span>
                  </button>
                  <button
                    className={`sd-onboard-choice-card ${connectionType === 'gateway' ? 'is-selected' : ''}`}
                    onClick={() => chooseConnectionType('gateway')}
                    type="button"
                  >
                    <span className="sd-onboard-choice-top">
                      <span className="sd-onboard-choice-icon is-gateway">
                        <GatewayBridgeIcon />
                      </span>
                      <span className="sd-onboard-choice-check" aria-hidden="true" />
                    </span>
                    <strong>Gateway Bridge</strong>
                    <small>A bridge payload carries one or more virtual devices through one MQTT topic.</small>
                    <span className="sd-onboard-choice-points">
                      <span>Many sensors</span>
                      <span>Remote sites</span>
                    </span>
                  </button>
                </div>

                <div className="sd-onboard-form-section">
                  <h3>Device name</h3>
                  <div className="sd-onboard-field-grid is-single">
                    <label className="sd-field">
                      <span>Device Name</span>
                      <input
                        placeholder="e.g. North Roof Temp Sensor"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                      <small>Use a clear name operators can find later.</small>
                    </label>
                  </div>
                </div>

                {connectionType === 'gateway' && (
                  <div className="sd-onboard-gateway-block">
                    <div className="sd-onboard-mini-head">
                      <h3>Gateway parser</h3>
                      <p>Bind this virtual asset to a broker topic and payload identifier.</p>
                    </div>
                    <div className="sd-onboard-field-grid">
                      <label className="sd-field">
                        <span>Gateway MQTT Topic (publish target)</span>
                        <input
                          placeholder="e.g. factory/gw-001 or vampfi/site-a"
                          value={gatewayTopic}
                          onChange={(event) => setGatewayTopic(event.target.value)}
                        />
                        <small>The topic your gateway publishes payloads to. This is the ONLY topic the device credentials are allowed to publish on — it is different from the MQTT username.</small>
                      </label>
                      <label className="sd-field">
                        <span>Device Identifier</span>
                        <input
                          placeholder="e.g. 493C250702069207 or VI_9207"
                          value={deviceIdentifier}
                          onChange={(event) => setDeviceIdentifier(event.target.value)}
                        />
                        <small>Unique value inside the gateway payload that identifies this virtual asset.</small>
                      </label>
                    </div>
                    <label className="sd-field">
                      <span>Parser Type</span>
                      <select
                        value={gatewayParserType}
                        onChange={(event) => setParserType(event.target.value as ParserType)}
                      >
                        <option value="devId_field">devId Field</option>
                        <option value="json_path">JSON Path</option>
                        <option value="custom">Custom Field Mapping</option>
                      </select>
                      <small className="sd-onboard-hint">{parserCopy[gatewayParserType]}</small>
                    </label>

                    {gatewayParserType === 'custom' && (
                      <label className="sd-field">
                        <span>Parser Config JSON</span>
                        <textarea
                          placeholder='{"temperature_field":"temp","humidity_field":"hum","battery_field":"bat"}'
                          value={parserConfigJson}
                          onChange={(event) => setParserConfigJson(event.target.value)}
                          rows={5}
                        />
                        <small className="sd-onboard-hint">Valid JSON object that maps incoming payload keys to Seedronics metric fields.</small>
                      </label>
                    )}
                  </div>
                )}

                <div className="sd-onboard-actions">
                  <button
                    className="sd-primary-button"
                    disabled={!isStep1Valid() || previewLoading}
                    onClick={goToPreview}
                    type="button"
                  >
                    {previewLoading ? 'Generating topic...' : 'Continue'}
                  </button>
                </div>

                <details
                  className="sd-onboard-optional-drawer"
                  onToggle={(event) => setOptionalOpen(event.currentTarget.open)}
                  open={optionalOpen}
                >
                  <summary>
                    <span>Location & heartbeat</span>
                    <small>Optional setup details</small>
                  </summary>
                  <div className="sd-onboard-optional-body">
                    <div className="sd-onboard-form-section">
                      <h3>Location detail</h3>
                      <div className="sd-onboard-field-grid">
                        <label className="sd-field">
                          <span>Location Detail</span>
                          <input
                            placeholder="e.g. Rack 3, near compressor"
                            value={location}
                            onChange={(event) => setLocation(event.target.value)}
                          />
                          <small>Optional physical note for field teams.</small>
                        </label>
                        {sites.length > 0 && (
                          <label className="sd-field">
                            <span>Site</span>
                            <select
                              value={siteId}
                              onChange={(event) => {
                                setSiteId(event.target.value);
                                setAreaId('');
                              }}
                            >
                              <option value="">Unassigned</option>
                              {sites.map((site) => (
                                <option key={site.id} value={site.id}>
                                  {site.name}
                                </option>
                              ))}
                            </select>
                            <small>Optional facility assignment.</small>
                          </label>
                        )}
                        {sites.length > 0 && (
                          <label className="sd-field">
                            <span>Area</span>
                            <select
                              value={areaId}
                              onChange={(event) => setAreaId(event.target.value)}
                              disabled={!siteId}
                            >
                              <option value="">Unassigned</option>
                              {(selectedSite?.areas || []).map((area) => (
                                <option key={area.id} value={area.id}>
                                  {area.name}
                                </option>
                              ))}
                            </select>
                            <small>Optional area inside the selected site.</small>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="sd-onboard-form-section">
                      <h3>Health policy</h3>
                      <label className="sd-field sd-onboard-heartbeat">
                        <span>Heartbeat Timeout</span>
                        <input
                          placeholder="e.g. 30"
                          type="number"
                          min={1}
                          max={1440}
                          value={heartbeatTimeout}
                          onChange={(event) => setHeartbeatTimeout(event.target.value)}
                        />
                        <small className="sd-onboard-hint">
                          Switches to offline when no data arrives within this window.
                        </small>
                      </label>
                    </div>
                  </div>
                </details>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="sd-onboard-section-head">
                <span>{currentStep.label}</span>
                <h2>Confirm the MQTT route</h2>
                <p>Review the generated topic before Seedronics creates the device.</p>
              </div>
              <div className="sd-onboard-body">
                {error && <div className="sd-onboard-error">{error}</div>}

                <div className="sd-onboard-review-grid">
                  <ReviewItem label="Device Name" value={name} />
                  <ReviewItem label="Connection" value={connectionType === 'direct' ? 'Direct MQTT' : 'Gateway Virtual Device'} />
                  <ReviewItem label="Site / Area" value={[selectedSite?.name, selectedArea?.name].filter(Boolean).join(' / ') || 'Unassigned'} />
                  <ReviewItem label="Heartbeat" value={`${heartbeatTimeoutMinutes} minutes`} />
                  {connectionType === 'gateway' && (
                    <>
                      <ReviewItem label="Gateway Topic (publish target)" value={gatewayTopic} code />
                      <ReviewItem label="Device Identifier" value={deviceIdentifier} code />
                      <ReviewItem label="Parser" value={gatewayParserType} />
                    </>
                  )}
                  {connectionType === 'direct' && <ReviewItem label="Location Detail" value={location || 'Not specified'} />}
                </div>

                <div className="sd-onboard-topic-card">
                  <span>MQTT Username (auto-generated device topic)</span>
                  <code>{previewedTopic || 'Pending topic preview'}</code>
                </div>

                <div className="sd-onboard-actions">
                  <button className="sd-secondary-button" onClick={() => setStep(0)} type="button">
                    Back
                  </button>
                  <button className="sd-primary-button" onClick={handleCreate} disabled={loading} type="button">
                    {loading ? 'Creating...' : 'Create Device'}
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 2 && device && (
            <>
              <div className="sd-onboard-section-head">
                <span>{currentStep.label}</span>
                <h2>Copy the credential packet</h2>
                <p>Use these values in the sensor firmware or field commissioning app.</p>
              </div>
              <div className="sd-onboard-body">
                {error && <div className="sd-onboard-error">{error}</div>}

                <CredentialBlock
                  copied={copied}
                  label="MQTT Username (device topic)"
                  name="topic"
                  onCopy={copy}
                  value={device.topic}
                />
                <CredentialBlock
                  copied={copied}
                  label="API Key (MQTT password)"
                  name="key"
                  onCopy={copy}
                  value={device.api_key || ''}
                />

                {isGatewayDevice && (
                  <CredentialBlock
                    copied={copied}
                    label="Gateway Topic — publish telemetry HERE"
                    name="pubtopic"
                    onCopy={copy}
                    value={publishTopic}
                  />
                )}

                <CredentialBlock
                  copied={copied}
                  label="MQTT Broker"
                  name="broker"
                  onCopy={copy}
                  value={brokerUrl}
                />

                {tlsAvailable && (
                  <div className="sd-onboard-choice-grid" aria-label="Broker transport">
                    <button
                      className={`sd-onboard-choice-card ${useTls ? 'is-selected' : ''}`}
                      onClick={() => setUseTls(true)}
                      type="button"
                    >
                      <strong>TLS (MQTTS)</strong>
                      <em>Recommended — encrypted on port {mqttConfig?.tls_port}. Prefer this when the gateway supports TLS.</em>
                    </button>
                    <button
                      className={`sd-onboard-choice-card ${!useTls ? 'is-selected' : ''}`}
                      onClick={() => setUseTls(false)}
                      type="button"
                    >
                      <strong>Plain (MQTT)</strong>
                      <em>Fallback — unencrypted on port {mqttConfig?.port}, for gateways without TLS support.</em>
                    </button>
                  </div>
                )}

                <div className="sd-onboard-code-grid">
                  <CredentialBlock
                    copied={copied}
                    label="Full Config JSON"
                    name="json"
                    onCopy={copy}
                    value={configJson}
                    multiline
                  />
                  <CredentialBlock
                    copied={copied}
                    label="Node.js Publish Example"
                    name="code"
                    onCopy={copy}
                    value={codeExample}
                    multiline
                  />
                </div>

                <div className="sd-onboard-qr-row">
                  <div>
                    <strong>Mobile Setup QR</strong>
                    <span>Scan from a field laptop or setup phone to transfer the credential packet.</span>
                  </div>
                  <div className="sd-onboard-qr">
                    <QRCodeSVG value={configJson} size={isMobile ? 132 : 168} level="M" includeMargin />
                  </div>
                </div>

                <div className="sd-onboard-actions">
                  <button className="sd-primary-button" onClick={() => setStep(3)} type="button">
                    Finish
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 3 && device && (
            <div className="sd-onboard-done">
              <span className="sd-onboard-done-mark">OK</span>
              <h2>Device Registered</h2>
              <p>{device.name} is ready to connect and begin publishing telemetry.</p>
              <div className="sd-onboard-topic-card">
                <span>MQTT Username</span>
                <code>{device.topic}</code>
              </div>
              {isGatewayDevice && (
                <div className="sd-onboard-topic-card">
                  <span>Publish telemetry to (gateway topic)</span>
                  <code>{publishTopic}</code>
                </div>
              )}
              <div className="sd-onboard-topic-card">
                <span>API Key (MQTT password)</span>
                <code>{maskSecret(device.api_key)}</code>
              </div>
              <button className="sd-primary-button" onClick={resetWizard} type="button">
                Register Another Device
              </button>
            </div>
          )}
          </div>
        </article>

        <aside className="sd-onboard-companion" aria-label="Setup companion">
          <article className="sd-panel sd-onboard-companion-card">
            <div className="sd-onboard-companion-head">
              <div>
                <span>Setup Companion</span>
                <strong>Your progress</strong>
              </div>
              <b>Step {step + 1} of {STEPS.length}</b>
            </div>
            <div className="sd-onboard-progress-meter" aria-hidden="true">
              <span style={{ width: `${onboardingPercent}%` }} />
            </div>
            <p>{currentStep.detail}</p>
          </article>

          <article className="sd-panel sd-onboard-companion-card">
            <div className="sd-onboard-companion-head">
              <div>
                <span>Live Workspace Summary</span>
                <strong>Current selections</strong>
              </div>
            </div>
            <div className="sd-onboard-summary-body">
              <SummaryRow label="Mode" value={connectionType === 'direct' ? 'Direct sensor' : 'Gateway bridge'} />
              <SummaryRow label="Asset" value={device?.name || name || 'Unnamed device'} />
              <SummaryRow label="Site" value={selectedSite?.name || 'Unassigned'} />
              <SummaryRow label="Area" value={selectedArea?.name || 'Unassigned'} />
              <SummaryRow label="Heartbeat" value={`${heartbeatTimeoutMinutes} min`} />
              <SummaryRow label="MQTT Username" value={device?.topic || previewedTopic || 'Generated after preview'} code />
              {connectionType === 'gateway' && (
                <>
                  <SummaryRow label="Publish Topic" value={gatewayTopic || 'Required'} code />
                  <SummaryRow label="Identifier" value={deviceIdentifier || 'Required'} code />
                  <SummaryRow label="Parser" value={gatewayParserType} />
                </>
              )}
            </div>
          </article>

          <OnboardingChecklist variant="compact" />
        </aside>
      </div>
    </section>
  );
}

function DirectSensorIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" focusable="false">
      <rect x="16" y="10" width="16" height="28" rx="8" />
      <path d="M18.5 16.5h11" />
      <path d="M18.5 31.5h11" />
      <path d="M12 19.5a10 10 0 0 0 0 9" />
      <path d="M36 19.5a10 10 0 0 1 0 9" />
      <path d="M8 15a17 17 0 0 0 0 18" />
      <path d="M40 15a17 17 0 0 1 0 18" />
      <circle cx="24" cy="24" r="3" />
    </svg>
  );
}

function GatewayBridgeIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" focusable="false">
      <rect x="10" y="13" width="28" height="22" rx="5" />
      <path d="M17 35v4" />
      <path d="M31 35v4" />
      <path d="M18 22h12" />
      <path d="M18 28h7" />
      <circle cx="35" cy="25" r="5" />
      <path d="M39 21.5l4-4" />
      <path d="M39 28.5l4 4" />
      <circle cx="44" cy="16.5" r="2" />
      <circle cx="44" cy="33.5" r="2" />
    </svg>
  );
}

function OnboardingStepper({
  currentStep,
  onSelect,
}: {
  currentStep: number;
  onSelect: (step: number) => void;
}) {
  return (
    <div className="sd-onboard-stepper" aria-label="Onboarding steps">
      {STEPS.map((item, index) => (
        <button
          key={item.label}
          className={`${index === currentStep ? 'is-active' : ''} ${index < currentStep ? 'is-complete' : ''}`}
          disabled={index > currentStep}
          onClick={() => index <= currentStep && onSelect(index)}
          type="button"
        >
          <span className="sd-onboard-step-dot">{index < currentStep ? 'OK' : index + 1}</span>
          <span>
            <strong>{item.shortLabel}</strong>
            <small>{item.detail}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

function ReviewItem({ code, label, value }: { code?: boolean; label: string; value: string }) {
  return (
    <div className="sd-onboard-review-item">
      <span>{label}</span>
      {code ? <code>{value || '-'}</code> : <strong>{value || '-'}</strong>}
    </div>
  );
}

function SummaryRow({ code, label, value }: { code?: boolean; label: string; value: string }) {
  return (
    <div className="sd-onboard-summary-row">
      <span>{label}</span>
      {code ? <code>{value}</code> : <strong>{value}</strong>}
    </div>
  );
}

function CredentialBlock({
  copied,
  label,
  multiline,
  name,
  onCopy,
  value,
}: {
  copied: string | null;
  label: string;
  multiline?: boolean;
  name: string;
  onCopy: (value: string, label: string) => void;
  value: string;
}) {
  return (
    <div className={`sd-onboard-credential ${multiline ? 'is-multiline' : ''}`}>
      <div>
        <span>{label}</span>
        {multiline ? <pre>{value}</pre> : <code>{value}</code>}
      </div>
      <button className="sd-secondary-button" onClick={() => onCopy(value, name)} type="button">
        {copied === name ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function maskSecret(value?: string) {
  if (!value) return '-';
  if (value.length <= 24) return value;
  return `${value.slice(0, 16)}...${value.slice(-8)}`;
}
